import { Element } from "@nkitaa/builder-engine";
import { motion } from "framer-motion";
import type { BorderSide, RuntimePropsWithChildren } from "./shared";
import {
  CARD_HOVER_PROPS,
  SECTION_REVEAL_PROPS,
  ThemeSectionContainer,
  classNames,
  extractUtilityClasses,
  getBorderClasses,
  getGridClasses,
  isVisible,
  parseContentAlignmentClass,
  parseHeroOppositeWidth,
  parseHeroWidth,
  sanitizeImageSrc,
  toCssLength,
} from "./shared";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import { TextChild } from "./textChild";
import { type WBReveal } from "./reveal";

export function TestimonialsChild({
  isVisible = true,
  imageUrl,
  imageUrlBottom,
  // The EDITOR gates these images on the boolean; the runtime gated on URL
  // presence alone, so a testimonial the organiser saw hidden was published
  // showing. Ten templates ship `false` alongside a real url, which is
  // exactly where the two disagreed. Default `true` so anything that never
  // set the flag is unaffected.
  isVisibleImageTop = true,
  isVisibleImageBottom = true,
  isVisibleContent,
  textContent,
  fontSizeContent = "text-sm",
  fontFamilyContent,
  textAlignContent = "left",
  fontColorContent,
  isVisibleName,
  textName,
  fontSizeName = "text-lg",
  fontFamilyName,
  textAlignName = "left",
  fontColorName,
  isVisibleOccupation,
  textOccupation,
  fontSizeOccupation = "text-md",
  fontFamilyOccupation,
  textAlignOccupation = "left",
  fontColorOccupation,
  padding = "p-3 wb-md:p-4 wb-lg:p-5",
  distElements = "gap-3",
  backgroundColor,
  border = false,
  borderColor,
  borderWidth,
  borderStyle,
  borderSides,
  borderRadius,
}: {
  isVisible?: boolean;
  imageUrl?: string;
  imageUrlBottom?: string;
  isVisibleImageTop?: boolean;
  isVisibleImageBottom?: boolean;
  isVisibleContent?: boolean;
  textContent?: string;
  fontSizeContent?: string;
  fontFamilyContent?: string;
  textAlignContent?: "left" | "center" | "right" | "justify";
  fontColorContent?: string;
  isVisibleName?: boolean;
  textName?: string;
  fontSizeName?: string;
  fontFamilyName?: string;
  textAlignName?: "left" | "center" | "right" | "justify";
  fontColorName?: string;
  isVisibleOccupation?: boolean;
  textOccupation?: string;
  fontSizeOccupation?: string;
  fontFamilyOccupation?: string;
  textAlignOccupation?: "left" | "center" | "right" | "justify";
  fontColorOccupation?: string;
  padding?: string;
  distElements?: string;
  backgroundColor?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: ("top" | "right" | "bottom" | "left")[];
  borderRadius?: string;
}) {
  const { currentRounding, currentColorPalette } = useWBTheme();
  if (!isVisible) return null;
  const topLogoUrl = sanitizeImageSrc(imageUrl);
  const borderCls = getBorderClasses(border, borderWidth || "border", borderStyle || "border-solid", borderSides);
  const roundedCls = extractUtilityClasses(borderRadius ?? currentRounding, "rounded");
  return (
    <motion.div
      {...CARD_HOVER_PROPS}
      className={classNames(
        "w-full h-full flex flex-col",
        padding,
        distElements,
        borderCls,
        roundedCls,
      )}
      style={{
        transition: "background-color 300ms ease-in-out, border-color 300ms ease-in-out",
        backgroundColor: backgroundColor || "transparent",
        borderColor: borderColor ?? currentColorPalette.secondary,
        borderRadius: roundedCls ? undefined : toCssLength(borderRadius ?? currentRounding),
      }}
    >
      {isVisibleImageTop && topLogoUrl ? (
        <div style={{ width: "8rem" }}>
          <img
            src={topLogoUrl}
            alt="testimonial logo"
            style={{ width: "100%", height: "auto", objectFit: "contain" }}
          />
        </div>
      ) : null}
      <TextChild
        isVisible={isVisibleContent}
        text={textContent}
        fontSize={fontSizeContent}
        fontFamily={fontFamilyContent}
        textAlign={textAlignContent}
        color={fontColorContent}
      />
      <div style={{ display: "flex", flexDirection: "row", gap: "0.75rem" }}>
        {isVisibleImageBottom && imageUrlBottom ? (
          <img
            src={sanitizeImageSrc(imageUrlBottom)}
            alt={textName ?? "person"}
            style={{
              width: "4rem",
              height: "4rem",
              borderRadius: "999px",
              flexShrink: 0,
            }}
          />
        ) : null}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            flex: 1,
            minWidth: 0,
          }}
        >
          <TextChild
            isVisible={isVisibleName}
            text={textName}
            fontSize={fontSizeName}
            fontFamily={fontFamilyName}
            textAlign={textAlignName}
            color={fontColorName}
            headingLevel="h2"
          />
          <TextChild
            isVisible={isVisibleOccupation}
            text={textOccupation}
            fontSize={fontSizeOccupation}
            fontFamily={fontFamilyOccupation}
            textAlign={textAlignOccupation}
            color={fontColorOccupation}
          />
        </div>
      </div>
    </motion.div>
  );
}

export function Testimonials(props: RuntimePropsWithChildren) {
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const reveal = props.reveal as WBReveal | undefined;
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const distHeadlineHeader =
    (props.distHeadlineHeader as string | undefined) ?? "mb-2";
  const distHeaderText = (props.distHeaderText as string | undefined) ?? "mb-2";
  const distTextTestimonials =
    (props.distTextTestimonials as string | undefined) ?? "gap-3";
  const distTestimonials =
    (props.distTestimonials as string | undefined) ?? "gap-3";
  const layoutWidth =
    (props.layoutWidth as "100%" | "75%" | "50%" | "33%" | "25%" | undefined) ??
    "50%";
  const inversed = (props.inversed as boolean | undefined) ?? false;
  const gridColumns =
    (props.gridColumns as "2" | "3" | "4" | "5" | "6" | undefined) ?? "3";
  const alignmentClass = parseContentAlignmentClass(
    props.contentAlignment as "top" | "center" | "bottom" | undefined,
  );
  const leftWidth = parseHeroWidth(layoutWidth);
  const rightWidth = parseHeroOppositeWidth(layoutWidth);
  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={padding}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
      reveal={reveal}
    >
      <motion.div
        {...SECTION_REVEAL_PROPS}
        className={classNames(
          "items-center max-w-275 flex flex-col w-full",
          alignmentClass,
          (props.isVisibleHeadline as boolean | undefined) ||
            (props.isVisibleHeader as boolean | undefined) ||
            (props.isVisibleText as boolean | undefined)
            ? "gap-5"
            : undefined,
        )}
      >
        <div
          className={classNames(
            "flex w-full",
            distTextTestimonials,
            layoutWidth !== "100%"
              ? inversed
                ? "flex-col wb-md:flex-row-reverse gap-4"
                : "flex-col wb-md:flex-row gap-4"
              : inversed
                ? "flex-col-reverse"
                : "flex-col",
          )}
        >
          <div
            className={classNames(
              "flex flex-col grow justify-center",
              leftWidth,
            )}
          >
            <div className={distHeadlineHeader}>
              <Element
                is={TextChild}
                id="headline"
                isVisible={props.isVisibleHeadline as boolean | undefined}
                text={props.textHeadline as string | undefined}
                fontSize={props.fontSizeHeadline as string | undefined}
                textAlign={
                  props.textAlignHeadline as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h2"
              />
            </div>
            <div className={distHeaderText}>
              <Element
                is={TextChild}
                id="header"
                isVisible={props.isVisibleHeader as boolean | undefined}
                text={props.textHeader as string | undefined}
                fontSize={props.fontSizeHeader as string | undefined}
                textAlign={
                  props.textAlignHeader as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h1"
              />
            </div>
            <Element
              is={TextChild}
              id="text"
              isVisible={props.isVisibleText as boolean | undefined}
              text={props.textText as string | undefined}
              fontSize={props.fontSizeText as string | undefined}
              textAlign={
                props.textAlignText as
                  | "left"
                  | "center"
                  | "right"
                  | "justify"
                  | undefined
              }
            />
          </div>
          <div
            className={classNames(
              getGridClasses(gridColumns, layoutWidth),
              distTestimonials,
              rightWidth,
            )}
          >
            {props.children}
          </div>
        </div>
      </motion.div>
    </ThemeSectionContainer>
  );
}
